import { TZDate } from '@date-fns/tz';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import CurvyOctagon from '../svg/CurvyOctagon.svg';
import {
  placeTicketsIntoWeekMarkers,
  type WeekMarker,
} from './ProgressionBar.utils';
import styles from './ProgressionBar.module.css';
import { BUSINESS_TIME_ZONE, Monthly10k } from '@daymade/common-lib/constants';
import { drawApiClient } from 'utils/apiClient';
import { tryApi } from 'utils/api/common';
import { useEffect, useState } from 'react';
import HttpStatusCode from 'utils/HttpStatusCode';

interface Props {
  shortMonthName: string | undefined;
  weekMarkers: (WeekMarker | undefined)[];
}
const weeksToRender = Array.from({ length: 5 }, (_, i) => i);

const ProgressionBar = (props: Props) => {
  const total = props.weekMarkers.reduce(
    (acc, x) => acc + (x?.earnings || 0),
    0,
  );

  return (
    <div className={styles.progressionBar + ' aboutUsTypographyCaption'}>
      <div className={styles.sideBySide}>
        <p className={styles.introText + ' aboutUsTypographyCaption'}>
          Automatically earn{' '}
          <strong>{Monthly10k.minimumEntriesPerWeek} entries</strong> every week
        </p>
        <ol className={styles.barContainer}>
          {weeksToRender.map((x) => {
            return (
              <li
                className={`${styles.week} ${props.weekMarkers[x]?.earnings ? styles.highlighted : ''} ${props.weekMarkers[x]?.isMidweek ? styles.midWeek : ''}`}
              >
                <div className={styles.weekLabel}>
                  {props.shortMonthName || '\u00A0'}{' '}
                  {props.weekMarkers[x]?.dayNumber}
                </div>
                <div className={`${styles.shapeContainer}`}>
                  <CurvyOctagon className={styles.shape} />
                  {props.weekMarkers[x]?.earnings ? (
                    <span className={styles.pointsEarnt}>
                      +{props.weekMarkers[x].earnings}{' '}
                      <span className="visuallyHidden">points earnt</span>
                    </span>
                  ) : (
                    <span className={styles.pointsEarnt}>
                      {props.shortMonthName
                        ? '+' + Monthly10k.minimumEntriesPerWeek
                        : '\u00A0'}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      <div className={styles.summaryText}>
        Total
        <strong className={styles.summaryTextAlt}>
          {props.shortMonthName ? total : '\u00A0'}
        </strong>
        <span className={styles.summaryTextAlt}>entries</span>
      </div>
    </div>
  );
};

export const CurrentUserProgressionBar = observer(() => {
  const userStore = useUserStore();

  // Find the tickets we're interested in
  const bigRaffleTickets = userStore.activeTickets.filter(
    (x) => x.drawType.code == Monthly10k.drawTypeCode,
  );

  // Render the progression bar as the draw date for the raffle tickets that we're interested in, if we have some
  const [drawDate, setDrawDate] = useState<Date | undefined>(
    bigRaffleTickets[0]?.draw?.drawDate || undefined,
  );

  // Otherwise, make a request to figure out the current monthly 10k giveaway draw date
  useEffect(() => {
    if (!drawDate) {
      (async () => {
        const draw = await tryApi(
          drawApiClient.public.getDrawTypeCached({
            params: { draw_code: Monthly10k.drawTypeCode },
          }),
        );
        if (draw.status === HttpStatusCode.OK) {
          setDrawDate(draw.body.next_draw?.drawDate || undefined);
        }
      })();
    }
  }, []);

  // If we don't know the draw date, then render the progression bar without much detail
  if (!drawDate) {
    return <ProgressionBar shortMonthName={undefined} weekMarkers={[]} />;
  }
  const startOfMonth = new TZDate(
    drawDate.getFullYear(),
    drawDate.getMonth(),
    1,
    0,
    0,
    0,
    0,
    BUSINESS_TIME_ZONE,
  );

  const shortMonthName = new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(startOfMonth);

  const days =
    /* Note that February is 1 rather than zero, as months are zero-indexed */
    startOfMonth.getMonth() === 1
      ? Monthly10k.entryAwardDayOfMonthFeb
      : Monthly10k.entryAwardDayOfMonthOther;

  const now = new Date();
  const weekMarkers: WeekMarker[] = days.map((x) => ({
    dayNumber: x,
    earnings: 0,
    isMidweek: now.getDate() != x,
  }));

  placeTicketsIntoWeekMarkers(startOfMonth, bigRaffleTickets, weekMarkers);

  return (
    <ProgressionBar shortMonthName={shortMonthName} weekMarkers={weekMarkers} />
  );
});

export default ProgressionBar;
